import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    Drawer,
    Typography,
} from '@material-ui/core';
import { Close, ExpandMore, SwapHoriz } from '@material-ui/icons';
import timeService from '../../../services/TimeService';
import ShiftTemplateSegmentService from '../../../services/ShiftTemplateSegmentService';
import { getShiftDisplayName } from '../../../services/ShiftDisplayService';
import {
    DaysOfWeekNames,
    PeriodType,
    ShiftDisplayType,
} from '../../../Enums';
import './DetailedSchedulePattern.scss';

/**
 * @param {{
 *  maxNbWeek: number,
 *  isOpen: boolean,
 *  currentShiftDetailed: EmployeeBidProfile.IGroupedScheduleTemplateItems,
 *  shiftsDictionary: BidProfile.ISchedulePatternsShiftTemplateDictionary,
 *  onClose: Function,
 *  anchorDay: DayOfWeek,
 *  dayCount: number,
 *  periodType: PeriodType,
 *  daysPerPeriod: number,
 *  shiftDisplayType?: string,
 * }} props
 */
function DetailedSchedulePattern({
    maxNbWeek = 1, isOpen, currentShiftDetailed, shiftsDictionary, onClose, anchorDay = 0, dayCount, periodType, daysPerPeriod = 7, shiftDisplayType = ShiftDisplayType.ShiftLabel,
}) {
    const currentShiftName = currentShiftDetailed?.workplanName || currentShiftDetailed?.schedulePatternTemplate.name || '';
    const orgJobSplit = currentShiftDetailed?.orgJobPath.split('/');
    const smallCurrentShiftOrgJobPath = `.../${orgJobSplit?.length >= 3
        ? orgJobSplit?.slice(-3).join('/')
        : currentShiftDetailed?.orgJobPath}`;
    /**
     * @param {string} shiftTemplateId
     * @returns {BidProfile.IShiftTemplate}
     */
    const getShiftTemplate = (shiftTemplateId) => shiftsDictionary[shiftTemplateId];
    /**
     * @param {number} index
     * @param {number} weekIndex
     */
    const generateDayDetailed = (index, weekIndex) => {
        const isPeriodTypeDay = periodType === PeriodType.Days;
        const labelDayIndex = isPeriodTypeDay ? index : (index + anchorDay) % daysPerPeriod;
        const dayLabel = isPeriodTypeDay ? `Day ${index + 1}` : DaysOfWeekNames[labelDayIndex];
        return (
            <div className="dsp-detailedShiftContainer">
                {currentShiftDetailed.shifts[weekIndex] && currentShiftDetailed.shifts[weekIndex][index].length
                    ? (
                        <div className="detailedDayContainer">
                            <div className="detailedDayLabel">{dayLabel}</div>
                            <div className="detailedDayShift">
                                {
                                    currentShiftDetailed.shifts[weekIndex][index].map((shift) => {
                                        const shiftTemplate = getShiftTemplate(shift.shiftTemplate);
                                        return (
                                            <Accordion
                                                key={shiftTemplate.id}
                                                elevation={0}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore />}
                                                    className="detailedAccordionSummary"
                                                >
                                                    <div className="singleShiftSign"> </div>
                                                    <div className="detailedShiftHours">
                                                        {getShiftDisplayName(shiftTemplate, shiftDisplayType)}
                                                    </div>
                                                    {ShiftTemplateSegmentService.hasTransfer(shiftTemplate.segments) && <SwapHoriz />}
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <div className="detailedInfoContainer">
                                                        <div>
                                                            {
                                                                shiftTemplate.segments.map((x) => {
                                                                    const segmentType = x.segmentTypeId === 1
                                                                        ? ''
                                                                        : x.segmentTypeQualifier.split('_')[0];
                                                                    return (
                                                                        <div key={`segments_${x.id}`}>
                                                                            <div className="detailedSegment">
                                                                                <div className="detailedSegmentStartTime">
                                                                                    {timeService.calculateTimes(x.startTime)}
                                                                                </div>
                                                                                {`[${timeService.getTimeDifference(x.startTime, x.endTime)}]`}
                                                                                <div className="detailedSegmentType">{segmentType}</div>
                                                                            </div>
                                                                            <div className="detailed-job-location">
                                                                                {x.segmentTypeId === 1
                                                                                    && <div>{smallCurrentShiftOrgJobPath}</div>}
                                                                                {x.segmentTypeId === 3
                                                                                    && ShiftTemplateSegmentService
                                                                                        .generateShortTransferStrings(
                                                                                            x.transferString || '',
                                                                                            currentShiftDetailed.orgJobPath,
                                                                                        )
                                                                                        .map((s, i) => <div key={`${x.id}-${i}`}>{s}</div>)}
                                                                                {
                                                                                    x.segmentSCProfiles && x.segmentSCProfiles.length && (
                                                                                        <div>
                                                                                            <div>Skill and Certification Profiles</div>
                                                                                            {x.segmentSCProfiles.map((x) => (<div key={x.id}>{x.label}</div>))}
                                                                                        </div>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </div>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    )
                    : (
                        <div className="detailedEmptyDayContainer">
                            <div className="detailedDayLabel">{dayLabel}</div>
                            <div className="detailedEmptyShift"> </div>
                        </div>
                    )}
            </div>
        );
    };
    /**
     * @param {number} weekIndex
     */
    const generateDetailedWeek = (weekIndex) => {
        const weekShifts = [<></>];
        for (let index = 0; index < daysPerPeriod; index++) {
            weekShifts.push(generateDayDetailed(index, weekIndex));
        }
        return (
            <div className="detailedWeekContainer">
                <Typography
                    className="detailedWeekLabel"
                    variant="h6"
                >
                    {`Week ${weekIndex + 1}`}
                </Typography>
                {weekShifts}
            </div>
        );
    };

    const generateDetailedDays = () => {
        const weekShifts = [<></>];
        for (let index = 0; index < dayCount; index++) {
            weekShifts.push(generateDayDetailed(index, Math.floor(index / daysPerPeriod)));
        }
        return (
            <div className="detailedWeekContainer">
                {weekShifts}
            </div>
        );
    };

    return (
        <Drawer
            anchor="bottom"
            open={isOpen}
            onClose={() => onClose()}
        >
            <div className="dsp-detailedDrawerContainer">
                <div className="detailedDrawerFirstRow">
                    <div className="shiftTitleText">{currentShiftName}</div>
                    <IconButton
                        color="inherit"
                        component="span"
                        onClick={() => onClose()}
                    >
                        <Close />
                    </IconButton>
                </div>
                <Typography variant="subtitle1">{smallCurrentShiftOrgJobPath}</Typography>
                <div className="detailedWeeksContainer">
                    {
                        periodType === PeriodType.Days
                            ? generateDetailedDays()
                            : Array.from(Array(maxNbWeek).keys()).map((x) => generateDetailedWeek(x))
                    }
                </div>
            </div>
        </Drawer>
    );
}
export default DetailedSchedulePattern;
